/**
 * Billing terms of a recurring schedule (tracker item 34): per visit, or a
 * flat contract with its amount and billing period. Used by the create form
 * and the schedule page's "Edit billing" form.
 */

import React from 'react';
import * as types from '../api/types';
import { inputClass, labelClass } from '../components/formStyles';
import { BILLING_FREQUENCY_LABELS, BillingForm, billingIntervalUnit, parseInterval } from './recurringUi';

export function RecurringBillingFields({ idPrefix, form, errors, onChange }: {
  idPrefix: string;
  form: BillingForm;
  /** Shown under the inputs (pass {} until the user tries to submit) */
  errors: Partial<Record<'amount' | 'interval', string>>;
  onChange: (patch: Partial<BillingForm>) => void;
}) {
  const interval = parseInterval(form.interval);
  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Billing" className="space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={idPrefix}
            className="mt-1"
            checked={form.model === 'PER_VISIT'}
            onChange={() => onChange({ model: 'PER_VISIT' })}
          />
          <span>Per visit: each generated job is invoiced like any other job</span>
        </label>
        <label className="flex items-start gap-2">
          <input
            type="radio"
            name={idPrefix}
            className="mt-1"
            checked={form.model === 'FLAT_CONTRACT'}
            onChange={() => onChange({ model: 'FLAT_CONTRACT' })}
          />
          <span>
            Flat contract: one invoice per billing period for a fixed amount, at the start of the period, however many visits there
            are. The visit jobs are covered by the contract and are never invoiced on their own.
          </span>
        </label>
      </div>

      {form.model === 'FLAT_CONTRACT' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 rounded-md border border-gray-200 p-4">
          <div>
            <label htmlFor={`${idPrefix}-amount`} className={labelClass}>Amount per period (before tax) *</label>
            <input
              id={`${idPrefix}-amount`}
              type="text"
              inputMode="decimal"
              className={inputClass}
              value={form.amount}
              placeholder="0.00"
              onChange={(event) => onChange({ amount: event.target.value })}
              aria-invalid={errors.amount ? true : undefined}
            />
            {errors.amount && <p className="mt-1 text-sm text-red-700">{errors.amount}</p>}
          </div>
          <div>
            <label htmlFor={`${idPrefix}-period`} className={labelClass}>Billing period *</label>
            <select
              id={`${idPrefix}-period`}
              className={inputClass}
              value={form.frequency}
              onChange={(event) => onChange({ frequency: event.target.value as types.FlatContractBillingFrequency })}
            >
              {types.FLAT_CONTRACT_BILLING_FREQUENCIES.map((value) => (
                <option key={value} value={value}>{BILLING_FREQUENCY_LABELS[value]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${idPrefix}-every`} className={labelClass}>Bill every</label>
            <div className="flex items-center gap-2">
              <input
                id={`${idPrefix}-every`}
                type="number"
                min={1}
                max={120}
                step={1}
                className={`${inputClass} max-w-[7rem]`}
                value={form.interval}
                onChange={(event) => onChange({ interval: event.target.value })}
                aria-invalid={errors.interval ? true : undefined}
              />
              <span className="text-sm text-gray-700">{billingIntervalUnit(form.frequency, Number.isInteger(interval) ? interval : 1)}</span>
            </div>
            {errors.interval && <p className="mt-1 text-sm text-red-700">{errors.interval}</p>}
          </div>
          <p className="sm:col-span-2 md:col-span-3 text-xs text-gray-500">
            Periods start on the schedule's start date (a monthly contract starting on the 15th bills on the 15th). Tax is added at
            the property's rate. The amount is billed in full each period (no proration).
          </p>
        </div>
      )}
    </div>
  );
}
